// bundles/livcg/panels/src/match-day/deck/DeckView.tsx
// Deck — a Metro-tile touch view. The layout lives in the user's PROFILE
// (GET/PUT /api/auth/prefs, users.prefs JSON). Saved only on "Saqlash".
import {
  userPrefsSchema,
  validateDeckTiles,
  type DeckTile,
  type ProjectState,
  type UserPrefs,
} from "@livcg/schema";
import type { EventType } from "@livcg/schema";
import { Check, Pencil, Plus, RotateCcw, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../shared/api.js";
import { sendRaw } from "../../shared/sendRaw.js";
import { useHotkeys } from "../../shared/useHotkeys.js";
import { Button, useToast } from "../../ui/index.js";
import { ClockControl } from "../control-room/ClockControl.js";
import { Drawer } from "../control-room/Drawer.js";
import { GridPanel } from "../control-room/GridPanel.js";
import { OverlayBoardView } from "../control-room/OverlayBoard.js";
import { PenaltyPanel } from "../control-room/PenaltyPanel.js";
import { PhaseNavigator } from "../control-room/PhaseNavigator.js";
import { PlayerCompareDrawer } from "../control-room/player-compare/PlayerCompareDrawer.js";
import { QuickEvents, type QuickEventsHandle } from "../control-room/QuickEvents.js";
import { ScoreControl } from "../control-room/ScoreControl.js";
import { SetupDrawer } from "../control-room/SetupDrawer.js";
import { ShortcutsHelp } from "../control-room/ShortcutsHelp.js";
import { useOverlayExtras } from "../control-room/useOverlayExtras.js";
import { TabLog } from "../TabLog.js";
import { TabStats } from "../TabStats.js";
import { useOverlaySwitch } from "../control-room/useOverlaySwitch.js";
import { useQuickEventChain } from "../control-room/useQuickEventChain.js";
import { DeckGrid } from "./DeckGrid.js";
import { DEFAULT_TILES, findFreeSpot } from "./layout.js";
import { WIDGET_DEFS, widgetDef, type DeckServices } from "./registry.js";
import { allOffSurfaces } from "../allOff.js";

type DeckPanel =
  | "events"
  | "overlay"
  | "log"
  | "match"
  | "lineup"
  | "stats"
  | "players"
  | "penalties"
  | "setup"
  | "shortcuts";

const DECK_PANELS: { id: DeckPanel; label: string; title: string; width?: string }[] = [
  { id: "events", label: "Hodisalar", title: "Hodisalar · almashtirish, gol turi", width: "max-w-4xl" },
  { id: "overlay", label: "Overlay", title: "Overlay boshqaruv", width: "max-w-4xl" },
  { id: "log", label: "Jurnal", title: "Hodisalar jurnali", width: "max-w-3xl" },
  { id: "match", label: "Soat·Hisob", title: "Soat, hisob, faza" },
  { id: "lineup", label: "Lineup", title: "Lineup · Joylashuv" },
  { id: "stats", label: "Statistika", title: "Statistika" },
  { id: "players", label: "O'yinchilar", title: "O'yinchilar", width: "max-w-4xl" },
  { id: "penalties", label: "Penalti", title: "Penaltilar seriyasi" },
  { id: "setup", label: "Sozlamalar", title: "Match sozlamalari" },
  { id: "shortcuts", label: "?", title: "Klaviatura yorliqlari", width: "max-w-md" },
];

let tileSeq = 0;
const newTileId = () => `t${Date.now().toString(36)}-${tileSeq++}`;

export function DeckView({ slug, state }: { slug: string; state: ProjectState }) {
  const { toast } = useToast();
  const extrasCtx = useOverlayExtras(state);
  const { extras } = extrasCtx;
  const overlay = useOverlaySwitch(slug, state, extras);
  // A single instance: shared between the EventsWidgets (home/away), otherwise
  // each would run its own timers and cause a collision (banner/score).
  const chain = useQuickEventChain(slug, state);

  const allOff = () => allOffSurfaces(slug, state);
  const services: DeckServices = { overlay, extras, allOff, chain };

  // Full panels from Control Room for what a tile cannot hold (subs, goal
  // type, log edits, lineup, stats, setup, penalties, player compare).
  const [panel, setPanel] = useState<DeckPanel | null>(null);
  const quickRef = useRef<QuickEventsHandle>(null);
  const fireEvent = (type: EventType) => {
    if (panel === "events") quickRef.current?.fire(type);
    else setPanel("events");
  };
  useHotkeys({
    space: () => void sendRaw("clock.toggle", { slug }),
    g: () => fireEvent("goal"),
    y: () => fireEvent("yellow_card"),
    r: () => fireEvent("red_card"),
    s: () => fireEvent("substitution"),
    // With a panel open, Esc only closes it (the Drawer handles that).
    esc: () => {
      if (!panel) overlay.hideCurrent();
    },
    "shift+esc": () => allOff(),
    "shift+/": () => setPanel("shortcuts"),
  });
  const panelBody: Record<DeckPanel, ReactNode> = {
    events: <QuickEvents slug={slug} state={state} controlRef={quickRef} chain={chain} wide />,
    overlay: (
      <OverlayBoardView
        slug={slug}
        state={state}
        onAllOff={allOff}
        showClassicOnly
        extrasCtx={extrasCtx}
        sw={overlay}
      />
    ),
    log: <TabLog slug={slug} state={state} />,
    match: (
      <div className="flex flex-col gap-3">
        <ClockControl slug={slug} state={state} />
        <ScoreControl slug={slug} state={state} />
        <PhaseNavigator slug={slug} state={state} />
      </div>
    ),
    lineup: (
      <GridPanel slug={slug} state={state} />
    ),
    stats: <TabStats slug={slug} state={state} />,
    players: <PlayerCompareDrawer slug={slug} state={state} />,
    penalties: <PenaltyPanel slug={slug} state={state} />,
    setup: <SetupDrawer slug={slug} state={state} onClose={() => setPanel(null)} />,
    shortcuts: <ShortcutsHelp />,
  };

  // --- Profile layout ---
  const [prefs, setPrefs] = useState<UserPrefs | null>(null);
  const [tiles, setTiles] = useState<DeckTile[]>(DEFAULT_TILES);
  const [loadWarn, setLoadWarn] = useState(false);
  // Not true until the prefs GET finishes: if "Tahrirlash" is opened in that window,
  // the draft is taken from DEFAULT_TILES over a layout that has not arrived yet (possibly
  // non-default), and "Saqlash" clobbers the user's real saved layout.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api
      .get<{ prefs: unknown }>("/api/auth/prefs")
      .then((res) => {
        if (!alive) return;
        const parsed = userPrefsSchema.safeParse(res.prefs ?? {});
        const p = parsed.success ? parsed.data : {};
        setPrefs(p);
        const layout = p.deckLayout;
        if (layout && layout.version === 1 && validateDeckTiles(layout.tiles) === null) {
          setTiles(layout.tiles);
        }
        setLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setLoadWarn(true); // continue with the default layout
        setPrefs({});
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  // --- Edit mode (draft) ---
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DeckTile[]>([]);
  const [saving, setSaving] = useState(false);
  const startEdit = () => {
    setDraft(tiles.map((t) => ({ ...t })));
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const resetDefault = () => setDraft(DEFAULT_TILES.map((t) => ({ ...t })));
  const save = async () => {
    if (saving) return;
    const err = validateDeckTiles(draft);
    if (err) {
      toast({ tone: "error", title: "Layout noto'g'ri", description: err });
      return;
    }
    setSaving(true);
    try {
      const nextPrefs: UserPrefs = { ...(prefs ?? {}), deckLayout: { version: 1, tiles: draft } };
      await api.put<{ ok: true }>("/api/auth/prefs", nextPrefs);
      setPrefs(nextPrefs);
      setTiles(draft);
      setEditing(false);
      toast({ tone: "success", title: "Deck layouti profilingizga saqlandi" });
    } catch {
      toast({ tone: "error", title: "Saqlash muvaffaqiyatsiz", description: "Qayta urinib ko'ring" });
    } finally {
      setSaving(false);
    }
  };

  const removeTile = (tileId: string) => setDraft((d) => d.filter((t) => t.id !== tileId));
  const addWidget = (widget: string) => {
    const def = widgetDef(widget);
    if (!def) return;
    const spot = findFreeSpot(draft, def.minW, def.minH);
    if (!spot) {
      toast({ tone: "error", title: "Bo'sh joy yo'q", description: "Avval biror plitkani olib tashlang" });
      return;
    }
    setDraft((d) => [...d, { id: newTileId(), widget, ...spot, w: def.minW, h: def.minH }]);
  };

  // Catalog: the ones not yet added (overlay/core — all without duplicates).
  const shownTiles = editing ? draft : tiles;
  const available = useMemo(() => {
    const placed = new Set(shownTiles.map((t) => t.widget));
    return WIDGET_DEFS.filter((d) => !placed.has(d.widget));
  }, [shownTiles]);
  const groups = useMemo(() => {
    const m = new Map<string, typeof available>();
    for (const d of available) {
      const arr = m.get(d.group) ?? [];
      arr.push(d);
      m.set(d.group, arr);
    }
    return [...m.entries()];
  }, [available]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="flex h-11 shrink-0 items-center justify-between gap-2 border-b border-divider px-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
            Deck
          </h2>
          {loadWarn && (
            <span className="rounded bg-card-amber/20 px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted">
              Profil sozlamalari o'qilmadi — standart layout
            </span>
          )}
        </div>
        {!editing && (
          <div className="flex min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto">
            {DECK_PANELS.map((d) => (
              <Button
                key={d.id}
                variant={panel === d.id ? "primary" : "ghost"}
                size="xs"
                onClick={() => setPanel(d.id)}
              >
                {d.label}
              </Button>
            ))}
          </div>
        )}
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={resetDefault} leadingIcon={<RotateCcw className="h-3.5 w-3.5" />}>
              Standart
            </Button>
            <Button variant="secondary" size="sm" onClick={cancelEdit} leadingIcon={<X className="h-3.5 w-3.5" />}>
              Bekor
            </Button>
            <Button variant="primary" size="sm" onClick={() => void save()} disabled={saving} leadingIcon={<Check className="h-3.5 w-3.5" />}>
              {saving ? "Saqlanmoqda…" : "Saqlash"}
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={startEdit}
            disabled={!loaded}
            leadingIcon={<Pencil className="h-3.5 w-3.5" />}
          >
            Tahrirlash
          </Button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        <DeckGrid
          slug={slug}
          state={state}
          tiles={shownTiles}
          editing={editing}
          services={services}
          onChange={setDraft}
          onRemove={removeTile}
        />
      </div>

      {DECK_PANELS.map((d) => (
        <Drawer
          key={d.id}
          open={panel === d.id}
          onClose={() => setPanel(null)}
          title={d.title}
          width={d.width ?? "max-w-2xl"}
        >
          {panel === d.id && panelBody[d.id]}
        </Drawer>
      ))}

      {editing && (
        <footer className="max-h-44 shrink-0 overflow-y-auto border-t border-divider bg-surface/50 p-3">
          <div className="flex flex-col gap-2">
            {groups.map(([group, defs]) => (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="w-24 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
                  {group}
                </span>
                {defs.map((d) => (
                  <Button key={d.widget} variant="ghost" size="xs" onClick={() => addWidget(d.widget)} leadingIcon={<Plus className="h-3 w-3" />}>
                    {d.label}
                  </Button>
                ))}
              </div>
            ))}
            {available.length === 0 && (
              <span className="text-[11px] text-ink-faint">Barcha widgetlar joylashtirilgan</span>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}
